import sv from './sv.json'
import en from './en.json'
import uk from './uk.json'
import es from './es.json'
import fi from './fi.json'
import type { FundraisingMessages } from '../i18n/messages'

// Canonical default catalogs shipped with the package. All five are translated, except the
// BankID/autogiro keys in `uk` which stay English on purpose (that flow is Swedish-site-only).
// A host that doesn't register fundraisingBake can override any string via its own `t` (app
// values win in withFundraisingMessages); a baking host's useFundraisingT reads the bake first.
export const fundraisingMessages = { sv, en, uk, es, fi } as const
export type FundraisingLocale = keyof typeof fundraisingMessages

/**
 * Merge the package catalogs into a host's message object under the `fundraising`
 * namespace, so the host's existing i18n (`t`) resolves `fundraising.<key>`.
 * App-supplied values WIN on key conflicts (override defaults).
 *
 * @example
 *   // Nuxt plugin (host has vue-i18n)
 *   i18n.mergeLocaleMessage(locale, { fundraising: fundraisingMessages[locale] })
 *   // Astro / plain-Vue host (no i18n engine) — use the bundled translator factory
 *   app.provide('t', makeFundraisingT(locale))
 */
export function withFundraisingMessages<T extends Record<string, unknown>>(
  appMessages: T,
  locale: FundraisingLocale,
): T & { fundraising: FundraisingMessages } {
  const base = fundraisingMessages[locale] ?? fundraisingMessages.sv
  const appNamespace = (appMessages.fundraising as Partial<FundraisingMessages>) ?? {}
  return { ...appMessages, fundraising: { ...base, ...appNamespace } }
}

/**
 * The catalog a site language uses; throws for a language the package ships no catalog for.
 * `Object.hasOwn`, not `in`: `'toString' in {}` is true.
 */
export function fundraisingLocaleFor(lang: string): FundraisingLocale {
  if (Object.hasOwn(fundraisingMessages, lang)) return lang as FundraisingLocale
  throw new Error(
    `[fundraising-ui] no fundraising catalog for "${lang}"; add src/locales/${lang}.json`,
  )
}
